import * as React from "react";

import ClassNames from "classnames";

import Styles from "./Tooltip.module.css";

type Positions = "right" | "left" | "top" | "bottom";

export interface Props {
  children: any;
  hoverContent: JSX.Element | string;
  tooltipClass?: string;
  className?: string;
  underline?: boolean;
  description?: string;
  staticPosition?: Positions;
}

const Tooltip: React.FC<Props> = props => {
  const {
    children,
    hoverContent,
    className,
    tooltipClass,
    underline,
    description,
    staticPosition
  } = props;

  const [position, setPosition] = React.useState<Positions>("bottom");
  const containerRef = React.useRef();
  const tooltipRef = React.useRef();

  React.useEffect(() => {
    let containerRect: any;
    let tooltipRect: any;
    containerRect = containerRef.current;
    tooltipRect = tooltipRef.current;

    if (staticPosition) {
      setPosition(staticPosition);
    } else if (tooltipRect != null) {
      containerRect = containerRect.getBoundingClientRect();
      tooltipRect = tooltipRect.getBoundingClientRect();

      const docWidth = document.documentElement.clientWidth;
      const docHeight = document.documentElement.clientHeight;

      const rx = containerRect.x + containerRect.width; // most right x
      const lx = containerRect.x; // most left x
      const ty = containerRect.y; // most top y
      const by = containerRect.y + containerRect.height; // most bottom y

      const spaceRight = rx + tooltipRect.width <= window.scrollX + docWidth;
      const spaceLeft = lx - tooltipRect.width >= 0;
      const spaceAbove = ty - tooltipRect.height >= 0;
      const spaceBellow = by + tooltipRect.height <= window.scrollY + docHeight;

      // dynamically set the position by available space
      if (!spaceLeft && spaceRight) {
        setPosition("right");
      } else if (!spaceRight && spaceLeft) {
        setPosition("left");
      } else if (spaceBellow) {
        setPosition("bottom");
      } else if (spaceAbove) {
        setPosition("top");
      }
    }
  }, [position]);

  return (
    <div
      className={ClassNames({
        [Styles.tooltip]: true,
        [Styles.underline]: underline,
        [Styles[position]]: true,
        [className]: className
      })}
      aria-describedby={description}
      title={description}
      tabIndex={0}
      ref={containerRef}
    >
      {children}
      <span
        role="tooltip"
        id={description}
        className={ClassNames({
          [Styles.text]: true,
          [Styles.hidden]: true,
          [Styles[position]]: true,
          [tooltipClass]: tooltipClass
        })}
        ref={tooltipRef}
      >
        {hoverContent}
      </span>
    </div>
  );
};

export default Tooltip;
